/**
 * Mixamo → cc-base clip retargeting pipeline.
 *
 * This module is a 1:1 port of the validated pipeline proved in
 * `public/hymotion-retarget-test.html` — the same skeleton picker, the
 * same regex-based bone-name map, the same retargeter options, the same
 * world-space hip-position post-processing. Any deviation from that page
 * is a bug.
 *
 * Why each step exists:
 *
 * 1. `buildBestSkeleton` scores connected bone components by how many of
 *    the 22 humanoid slots they cover, then tie-breaks on body-mesh
 *    overlap (so outfit/hair/face skeletons lose to the body skeleton on
 *    multi-skeleton rigs like MetaHuman). Single-component cc-base rigs
 *    pass through trivially.
 *
 * 2. `buildBoneNameMap` resolves SOURCE_PATTERNS against the donor's
 *    actual bone names and TARGET_PATTERNS against the loaded character's
 *    actual bone names — never literal lookups, always regex. This lets
 *    the same code work whether Lewis ships with `Hips` or `mixamorigHips`,
 *    whether the cc-base export uses `CC_Base_Hip` or a localised name, etc.
 *
 * 3. `processClipForCcRig`:
 *    a) Anchor the clip's `Hips.position` track to the source bind pose.
 *       HY-Motion's neutral hip is ~1 ft above bind; without this the
 *       character pops up the moment playback starts ("levitation").
 *    b) Strip ALL position tracks from the retargeter's input clone.
 *       upf-gti's own `retargetPositionTrack` Y-scales by trgY/srcY and
 *       multiplies by `trgEmbedWorldTransforms.inverse`, which mangles
 *       vertical motion when target armature axes/units differ from
 *       source. We replace it with a clean world-space delta.
 *    c) `retargetAnimation` does bind-pose-aware quaternion retargeting.
 *    d) Inject our own hip-position track using world-space deltas:
 *       `srcLocal → srcWorld → world delta from src bind → + trg bind
 *        world → trg local`. Axis- and unit-agnostic.
 *
 * Mixamo / Avaturn characters never go through this module — their tracks
 * already match their bones and `figure.loadFigure` skips it.
 */

import {
  AnimationClip,
  Group,
  Matrix4,
  MathUtils,
  Object3D,
  Quaternion,
  Skeleton,
  Vector3,
  VectorKeyframeTrack,
} from 'three';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { DRACOLoader } from 'three/examples/jsm/loaders/DRACOLoader.js';

import {
  AnimationRetargeting,
  applyTPose,
} from '../vendor/retargeting-threejs/retargeting.js';
import { type RigProfile } from './rig-profiles';

// ── Source skeleton donor (Lewis) ─────────────────────────────────────────
//
// HY-Motion outputs Mixamo-named clips calibrated against a Mixamo bind
// pose. To retarget them properly we need a Mixamo skeleton object as
// the `srcSkeleton` for `AnimationRetargeting`. We use Lewis.glb — already
// in the manifest, already used by the app for Mixamo characters.
//
// Loaded once on first non-Mixamo character load, cached for the rest of
// the session. We don't add Lewis's meshes to the scene — just pull a
// `Skeleton` instance from the parsed GLTF's bone tree.
//
// Critical: this loader path does NOT run `normalizeMixamoBones`, so the
// donor's bones keep whatever names ship in Lewis.glb (currently bare:
// `Hips`, `Spine`, …). The HY-Motion clip also arrives with bare track
// names. They match → retargeter works. Adding the `mixamorig` prefix on
// either side breaks the match and produces an empty clip.

const SOURCE_DONOR_URL = './models/characters/Lewis.glb';

let donorPromise: Promise<{ root: Group }> | null = null;

function gltfLoader(): GLTFLoader {
  // Match the loader configured in figure.ts — same DRACO decoder path,
  // since some characters (Bo) ship with Draco-compressed meshes. Lewis
  // doesn't strictly need it but loading both with the same loader keeps
  // behaviour consistent.
  const loader = new GLTFLoader();
  const draco = new DRACOLoader();
  draco.setDecoderPath('./draco/');
  loader.setDRACOLoader(draco);
  return loader;
}

async function loadSourceDonor(): Promise<{ root: Group }> {
  if (donorPromise) return donorPromise;
  donorPromise = (async () => {
    const gltf = await gltfLoader().loadAsync(SOURCE_DONOR_URL);
    const root = gltf.scene as unknown as Group;
    root.updateMatrixWorld(true);
    return { root };
  })();
  return donorPromise;
}

// ── Slot patterns (mirror of test page) ───────────────────────────────────
//
// Each slot maps to:
//   - `SOURCE_PATTERNS[slot]` — regex resolving the donor's actual bone
//     name. Tolerates `Hips`, `mixamorigHips`, `mixamorig:Hips` so the
//     same patterns work whether the donor was normalised or not.
//   - `TARGET_PATTERNS[profile][slot]` — regex resolving the loaded
//     character's actual bone name.

const SOURCE_PATTERNS: Record<string, RegExp> = {
  Hips:          /^(?:mixamorig:?)?Hips$/,
  Spine:         /^(?:mixamorig:?)?Spine$/,
  Spine1:        /^(?:mixamorig:?)?Spine1$/,
  Spine2:        /^(?:mixamorig:?)?Spine2$/,
  Neck:          /^(?:mixamorig:?)?Neck$/,
  Head:          /^(?:mixamorig:?)?Head$/,
  LeftShoulder:  /^(?:mixamorig:?)?LeftShoulder$/,
  LeftArm:       /^(?:mixamorig:?)?LeftArm$/,
  LeftForeArm:   /^(?:mixamorig:?)?LeftForeArm$/,
  LeftHand:      /^(?:mixamorig:?)?LeftHand$/,
  RightShoulder: /^(?:mixamorig:?)?RightShoulder$/,
  RightArm:      /^(?:mixamorig:?)?RightArm$/,
  RightForeArm:  /^(?:mixamorig:?)?RightForeArm$/,
  RightHand:     /^(?:mixamorig:?)?RightHand$/,
  LeftUpLeg:     /^(?:mixamorig:?)?LeftUpLeg$/,
  LeftLeg:       /^(?:mixamorig:?)?LeftLeg$/,
  LeftFoot:      /^(?:mixamorig:?)?LeftFoot$/,
  LeftToeBase:   /^(?:mixamorig:?)?LeftToeBase$/,
  RightUpLeg:    /^(?:mixamorig:?)?RightUpLeg$/,
  RightLeg:      /^(?:mixamorig:?)?RightLeg$/,
  RightFoot:     /^(?:mixamorig:?)?RightFoot$/,
  RightToeBase:  /^(?:mixamorig:?)?RightToeBase$/,
};

const CC_BASE_TARGET_PATTERNS: Record<string, RegExp> = {
  Hips:          /^CC_Base_Hip$/,
  Spine:         /^CC_Base_Waist$/,
  Spine1:        /^CC_Base_Spine01$/,
  Spine2:        /^CC_Base_Spine02$/,
  Neck:          /^CC_Base_NeckTwist01$/,
  Head:          /^CC_Base_Head$/,
  LeftShoulder:  /^CC_Base_L_Clavicle$/,
  LeftArm:       /^CC_Base_L_Upperarm$/,
  LeftForeArm:   /^CC_Base_L_Forearm$/,
  LeftHand:      /^CC_Base_L_Hand$/,
  RightShoulder: /^CC_Base_R_Clavicle$/,
  RightArm:      /^CC_Base_R_Upperarm$/,
  RightForeArm:  /^CC_Base_R_Forearm$/,
  RightHand:     /^CC_Base_R_Hand$/,
  LeftUpLeg:     /^CC_Base_L_Thigh$/,
  LeftLeg:       /^CC_Base_L_Calf$/,
  LeftFoot:      /^CC_Base_L_Foot$/,
  LeftToeBase:   /^CC_Base_L_ToeBase$/,
  RightUpLeg:    /^CC_Base_R_Thigh$/,
  RightLeg:      /^CC_Base_R_Calf$/,
  RightFoot:     /^CC_Base_R_Foot$/,
  RightToeBase:  /^CC_Base_R_ToeBase$/,
};

// Same hierarchy as CC_BASE_TARGET_PATTERNS but with the `CC_Base_` prefix
// stripped. Some Blender exports of CC4 characters lose the prefix during
// the FBX → GLB conversion (e.g. Anne Hathaway). Identical retargeting
// behaviour — only the names differ.
/** GLTFLoader may strip '.' (`root.x` → `rootx`). Accept both forms. */
function rigifyBone(stem: string, side: string): RegExp {
  const esc = stem.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  return new RegExp(`^${esc}(?:\\.${side}|${side})$`);
}

const RIGIFY_STRETCH_TARGET_PATTERNS: Record<string, RegExp> = {
  Hips:          rigifyBone('root', 'x'),
  Spine:         rigifyBone('spine_01', 'x'),
  Spine1:        rigifyBone('spine_02', 'x'),
  Spine2:        rigifyBone('spine_03', 'x'),
  Neck:          rigifyBone('neck', 'x'),
  Head:          rigifyBone('head', 'x'),
  LeftShoulder:  rigifyBone('shoulder', 'l'),
  LeftArm:       rigifyBone('arm_stretch', 'l'),
  LeftForeArm:   rigifyBone('forearm_stretch', 'l'),
  LeftHand:      rigifyBone('hand', 'l'),
  RightShoulder: rigifyBone('shoulder', 'r'),
  RightArm:      rigifyBone('arm_stretch', 'r'),
  RightForeArm:  rigifyBone('forearm_stretch', 'r'),
  RightHand:     rigifyBone('hand', 'r'),
  LeftUpLeg:     rigifyBone('thigh_stretch', 'l'),
  LeftLeg:       rigifyBone('leg_stretch', 'l'),
  LeftFoot:      rigifyBone('foot', 'l'),
  LeftToeBase:   rigifyBone('toes_01', 'l'),
  RightUpLeg:    rigifyBone('thigh_stretch', 'r'),
  RightLeg:      rigifyBone('leg_stretch', 'r'),
  RightFoot:     rigifyBone('foot', 'r'),
  RightToeBase:  rigifyBone('toes_01', 'r'),
};

const CC_BASE_NOPREFIX_TARGET_PATTERNS: Record<string, RegExp> = {
  Hips:          /^Hip$/,
  Spine:         /^Waist$/,
  Spine1:        /^Spine01$/,
  Spine2:        /^Spine02$/,
  Neck:          /^NeckTwist01$/,
  Head:          /^Head$/,
  LeftShoulder:  /^L_Clavicle$/,
  LeftArm:       /^L_Upperarm$/,
  LeftForeArm:   /^L_Forearm$/,
  LeftHand:      /^L_Hand$/,
  RightShoulder: /^R_Clavicle$/,
  RightArm:      /^R_Upperarm$/,
  RightForeArm:  /^R_Forearm$/,
  RightHand:     /^R_Hand$/,
  LeftUpLeg:     /^L_Thigh$/,
  LeftLeg:       /^L_Calf$/,
  LeftFoot:      /^L_Foot$/,
  LeftToeBase:   /^L_ToeBase$/,
  RightUpLeg:    /^R_Thigh$/,
  RightLeg:      /^R_Calf$/,
  RightFoot:     /^R_Foot$/,
  RightToeBase:  /^R_ToeBase$/,
};

// MetaHuman patterns are kept here so future profile work can reuse the
// same code path — currently `metahuman` rigs return null from
// `setupCcRetargeter` (no entry in TARGET_PATTERNS_BY_PROFILE means the
// caller treats them as unsupported). When we tackle MetaHuman in the
// main app, swap these in via a profile addition.

// Stock UE4/UE5 Mannequin skeleton ("Epic Skeleton") — pelvis + 3 spine
// bones (`spine_01..03`), `clavicle_l/r → upperarm_l/r → lowerarm_l/r →
// hand_l/r`, `thigh_l/r → calf_l/r → foot_l/r → ball_l/r`, `neck_01`,
// `head`. Used by Unreal Engine's Manny/Quinn and any character riding
// that skeleton (e.g. Viking warrior).
//
// A-pose at bind, so the same `applyTPose` + shoulder-spread treatment
// as `rigify-stretch` is required — registered in
// `RIG_PROFILES_NEED_APPLY_TPOSE` below.
const EPIC_SKELETON_TARGET_PATTERNS: Record<string, RegExp> = {
  Hips:          /^pelvis(_\d+)?$/,
  Spine:         /^spine_01(_\d+)?$/,
  Spine1:        /^spine_02(_\d+)?$/,
  Spine2:        /^spine_03(_\d+)?$/,
  Neck:          /^neck_01(_\d+)?$/,
  Head:          /^head(_\d+)?$/,
  LeftShoulder:  /^clavicle_l(_\d+)?$/,
  LeftArm:       /^upperarm_l(_\d+)?$/,
  LeftForeArm:   /^lowerarm_l(_\d+)?$/,
  LeftHand:      /^hand_l(_\d+)?$/,
  RightShoulder: /^clavicle_r(_\d+)?$/,
  RightArm:      /^upperarm_r(_\d+)?$/,
  RightForeArm:  /^lowerarm_r(_\d+)?$/,
  RightHand:     /^hand_r(_\d+)?$/,
  LeftUpLeg:     /^thigh_l(_\d+)?$/,
  LeftLeg:       /^calf_l(_\d+)?$/,
  LeftFoot:      /^foot_l(_\d+)?$/,
  LeftToeBase:   /^ball_l(_\d+)?$/,
  RightUpLeg:    /^thigh_r(_\d+)?$/,
  RightLeg:      /^calf_r(_\d+)?$/,
  RightFoot:     /^foot_r(_\d+)?$/,
  RightToeBase:  /^ball_r(_\d+)?$/,
};

const TARGET_PATTERNS_BY_PROFILE: Partial<Record<RigProfile, Record<string, RegExp>>> = {
  'cc-base':          CC_BASE_TARGET_PATTERNS,
  'cc-base-noprefix': CC_BASE_NOPREFIX_TARGET_PATTERNS,
  'rigify-stretch':   RIGIFY_STRETCH_TARGET_PATTERNS,
  'epic-skeleton':    EPIC_SKELETON_TARGET_PATTERNS,
};

/** Mixamo / HY-Motion source hip position tracks. */
const HIP_POS_TRACK_RE = /(?:^|[^a-zA-Z]|^mixamorig:?)Hips\.position$/i;

/**
 * Any rig's root hip position track (Mixamo `Hips`, CC `CC_Base_Hip`,
 * Rigify `rootx`, Epic Skeleton `pelvis`).
 *
 * Used by figure.ts for strip/anchor/locomotion on retargeted clips.
 * MUST cover every `trgHipName` that `processClipForCcRig` may emit
 * (i.e. `Hips`, `CC_Base_Hip`, `Hip`, `rootx`, `pelvis`) — otherwise
 * idle clips on that rig will float/drift because `lockClipInPlace`
 * fails to strip the surviving position track.
 */
export const HIP_POSITION_TRACK_RE =
  /(?:^|[^a-zA-Z]|^mixamorig:?)(?:Hips?|pelvis)\.position$|^root(?:\.x|x)\.position$/i;

/** Rigify A-pose needs explicit T-pose before bind-pose-aware retarget (see hymotion test). */
const RIG_PROFILES_NEED_APPLY_TPOSE: ReadonlySet<RigProfile> = new Set([
  'rigify-stretch',
  'epic-skeleton',
]);

/**
 * Extra shoulder abduction after `applyTPose` on rigs whose A-pose bind
 * leaves the arms slightly tucked vs Mixamo/Lewis. A few degrees outward
 * on each clavicle widens the bind so retargeted motion matches.
 *
 * Tuned on Lion Man (Rigify stretch); same default works for Viking
 * (Epic Skeleton). Tune per-character later if needed.
 */
const POST_TPOSE_SHOULDER_ABDUCTION_DEG = 2;

/**
 * Apply outward abduction to the resolved shoulder bones. Caller passes
 * the actual bone names from `auxNameMap.LShoulder` / `RShoulder` so this
 * works for any rig family (Rigify `shoulder.l`, Epic Skeleton
 * `clavicle_l`, etc.) without bone-name patterns.
 */
function spreadShoulders(
  trgRoot: Object3D,
  leftShoulderName: string | undefined,
  rightShoulderName: string | undefined,
): void {
  if (!leftShoulderName && !rightShoulderName) return;
  const rad = MathUtils.degToRad(POST_TPOSE_SHOULDER_ABDUCTION_DEG);
  const spread = new Quaternion();
  const axis = new Vector3(0, 0, 1);
  trgRoot.traverse((o) => {
    if (!(o as { isBone?: boolean }).isBone) return;
    if (leftShoulderName && o.name === leftShoulderName) {
      spread.setFromAxisAngle(axis, rad);
      o.quaternion.premultiply(spread);
      o.updateMatrix();
    } else if (rightShoulderName && o.name === rightShoulderName) {
      spread.setFromAxisAngle(axis, -rad);
      o.quaternion.premultiply(spread);
      o.updateMatrix();
    }
  });
  trgRoot.updateMatrixWorld(true);
}

const SLOT_TO_AUX: Record<string, string> = {
  Hips: 'Hips',
  Spine: 'BelowStomach',
  Spine1: 'Stomach',
  Spine2: 'ShouldersUnion',
  Neck: 'Neck',
  Head: 'Head',
  LeftShoulder: 'LShoulder',
  LeftArm: 'LArm',
  LeftForeArm: 'LElbow',
  LeftHand: 'LWrist',
  RightShoulder: 'RShoulder',
  RightArm: 'RArm',
  RightForeArm: 'RElbow',
  RightHand: 'RWrist',
  LeftUpLeg: 'LUpLeg',
  LeftLeg: 'LLeg',
  LeftFoot: 'LFoot',
  RightUpLeg: 'RUpLeg',
  RightLeg: 'RLeg',
  RightFoot: 'RFoot',
};

// ── Skeleton picker (1:1 with test page) ──────────────────────────────────
//
// Why this is more elaborate than "find the bone tree containing Hips":
//
// MetaHuman has multiple disjoint bone trees (face, body, outfit) all
// containing analogous bones. For cc-base / Mixamo there's typically one
// tree, but Mixamo FBX exports occasionally split SkinnedMesh objects in
// ways that surface as multiple "bone components" depending on how the
// loader walks them. Scoring by slot coverage + body-mesh overlap picks
// the right one in every case we've seen.
function buildBestSkeleton(
  root: Object3D,
  slotPatterns: Record<string, RegExp>,
): { skeleton: Skeleton | null; components: Array<{ rootName: string; count: number; score: number; bodyBoneOverlap: number }> } {
  const allBones: Object3D[] = [];
  root.traverse((o) => { if ((o as { isBone?: boolean }).isBone) allBones.push(o); });
  if (allBones.length === 0) return { skeleton: null, components: [] };

  const roots = allBones.filter((b) => !b.parent || !(b.parent as { isBone?: boolean }).isBone);
  const patterns = Object.values(slotPatterns);

  // Tie-breaker bias: bones referenced by SkinnedMesh objects whose name
  // looks like body geometry (and not outfit/hair/face). Equal slot
  // coverage between body skeleton and outfit skeleton would otherwise
  // pick whichever has more total bones — usually the outfit. Body-mesh
  // overlap correctly disambiguates.
  const bodyBoneSet = new Set<Object3D>();
  root.traverse((o) => {
    const sm = o as { isSkinnedMesh?: boolean; skeleton?: Skeleton; name?: string };
    if (!sm.isSkinnedMesh || !sm.skeleton || !sm.name) return;
    if (!/body|skin/i.test(sm.name)) return;
    if (/outfit|cloth|hair|tongue|teeth|eye/i.test(sm.name)) return;
    for (const b of sm.skeleton.bones) bodyBoneSet.add(b);
  });

  const components = roots.map((r) => {
    const bones: Object3D[] = [];
    r.traverse((o) => { if ((o as { isBone?: boolean }).isBone) bones.push(o); });
    const names = bones.map((b) => b.name);
    let score = 0;
    for (const p of patterns) if (names.some((n) => p.test(n))) score++;
    const bodyBoneOverlap = bones.reduce((n, b) => n + (bodyBoneSet.has(b) ? 1 : 0), 0);
    return { rootName: r.name, bones, count: bones.length, score, bodyBoneOverlap };
  });

  components.sort((a, b) =>
    b.score - a.score
    || b.bodyBoneOverlap - a.bodyBoneOverlap
    || b.count - a.count,
  );

  const winner = components[0];
  if (!winner || winner.score === 0) {
    return {
      skeleton: null,
      components: components.map((c) => ({ rootName: c.rootName, count: c.count, score: c.score, bodyBoneOverlap: c.bodyBoneOverlap })),
    };
  }
  return {
    skeleton: new Skeleton(winner.bones as never[]),
    components: components.map((c) => ({ rootName: c.rootName, count: c.count, score: c.score, bodyBoneOverlap: c.bodyBoneOverlap })),
  };
}

// ── Bone-name map (1:1 with test page) ────────────────────────────────────

function buildBoneNameMap(
  srcSkeleton: Skeleton,
  trgSkeleton: Skeleton,
  trgPatterns: Record<string, RegExp>,
): { boneNameMap: Record<string, string>; auxNameMap: Record<string, string> } {
  const srcNames = srcSkeleton.bones.map((b) => b.name);
  const trgNames = trgSkeleton.bones.map((b) => b.name);
  const boneNameMap: Record<string, string> = {};
  const auxNameMap: Record<string, string> = {};
  for (const slot of Object.keys(SOURCE_PATTERNS)) {
    const srcMatch = srcNames.find((n) => SOURCE_PATTERNS[slot].test(n));
    if (!srcMatch) continue;
    const trgPattern = trgPatterns[slot];
    if (!trgPattern) continue;
    const trgMatch = trgNames.find((n) => trgPattern.test(n));
    if (!trgMatch) continue;
    boneNameMap[srcMatch] = trgMatch;
    const aux = SLOT_TO_AUX[slot];
    if (aux) auxNameMap[aux] = trgMatch;
  }
  return { boneNameMap, auxNameMap };
}

// ── Public types ──────────────────────────────────────────────────────────

export interface CcRetargetContext {
  retargeter: AnimationRetargeting;
  /** Source hip bone literal name (e.g. `Hips`, `mixamorigHips`). */
  srcHipName: string;
  /** Target hip bone literal name (e.g. `CC_Base_Hip`). */
  trgHipName: string;
  /** Source hip local position at bind pose. */
  srcHipBindLocal: Vector3;
  /** Target hip local position at bind pose. */
  trgHipBindLocal: Vector3;
  /** Source hip world position at bind pose. */
  srcHipBindWorld: Vector3;
  /** Target hip world position at bind pose. */
  trgHipBindWorld: Vector3;
  /** Source hip parent's world matrix at bind pose. */
  srcHipParentMatrixWorld: Matrix4;
  /** Inverse of target hip parent's world matrix at bind pose. */
  trgHipParentMatrixWorldInv: Matrix4;
}

// ── Setup ─────────────────────────────────────────────────────────────────

/**
 * Build the retargeter for one loaded character.
 *
 * Called once per character load (when `rigProfile` is non-Mixamo). Returns
 * a context that `processClipForCcRig` consumes for every clip, or null
 * if the rig profile has no target patterns registered (e.g. metahuman).
 *
 * The donor is loaded on first call and cached. The target scene root is
 * what gets passed in — must be the loaded character's `figure.root`.
 * Bind-pose snapshots are taken before any clip plays, so the bones must
 * still be in their bind pose when this runs.
 */
export async function setupCcRetargeter(
  trgRoot: Object3D,
  rigProfile: RigProfile,
): Promise<CcRetargetContext | null> {
  const trgPatterns = TARGET_PATTERNS_BY_PROFILE[rigProfile];
  if (!trgPatterns) {
    console.warn(`[cc-retarget] no target patterns for profile "${rigProfile}" — skipping retargeter setup`);
    return null;
  }

  const donor = await loadSourceDonor();
  donor.root.updateMatrixWorld(true);
  trgRoot.updateMatrixWorld(true);

  const srcBuild = buildBestSkeleton(donor.root, SOURCE_PATTERNS);
  if (!srcBuild.skeleton) {
    console.warn('[cc-retarget] source skeleton resolution failed:', srcBuild.components);
    return null;
  }
  const srcSkeleton = srcBuild.skeleton;

  const trgBuild = buildBestSkeleton(trgRoot, trgPatterns);
  if (!trgBuild.skeleton) {
    console.warn('[cc-retarget] target skeleton resolution failed:', trgBuild.components);
    return null;
  }
  const trgSkeleton = trgBuild.skeleton;

  const { boneNameMap, auxNameMap } = buildBoneNameMap(srcSkeleton, trgSkeleton, trgPatterns);
  const resolved = Object.keys(boneNameMap).length;
  const total = Object.keys(SOURCE_PATTERNS).length;
  if (resolved === 0) {
    console.warn(`[cc-retarget] resolved 0/${total} bones — skipping retargeter`);
    return null;
  }
  console.log(`[cc-retarget] resolved ${resolved}/${total} bones for profile=${rigProfile}`);

  if (RIG_PROFILES_NEED_APPLY_TPOSE.has(rigProfile)) {
    try {
      applyTPose(trgSkeleton, auxNameMap);
      spreadShoulders(trgRoot, auxNameMap.LShoulder, auxNameMap.RShoulder);
      trgRoot.updateMatrixWorld(true);
      console.log(
        `[cc-retarget] applyTPose + shoulder spread ${POST_TPOSE_SHOULDER_ABDUCTION_DEG}° OK for profile=${rigProfile} (L=${auxNameMap.LShoulder}, R=${auxNameMap.RShoulder})`,
      );
    } catch (err) {
      console.warn(`[cc-retarget] applyTPose failed for profile=${rigProfile}:`, err);
      return null;
    }
  }

  let retargeter: AnimationRetargeting;
  try {
    retargeter = new AnimationRetargeting(srcSkeleton, trgSkeleton, {
      srcPoseMode: AnimationRetargeting.BindPoseModes.DEFAULT,
      trgPoseMode: AnimationRetargeting.BindPoseModes.CURRENT,
      trgEmbedWorldTransforms: true,
      boneNameMap,
    });
  } catch (err) {
    console.warn('[cc-retarget] AnimationRetargeting construction failed:', err);
    return null;
  }

  // Snapshot hip bind data — needed by the frame-0 anchor and the custom
  // world-space hip-position retarget. Taking these AFTER the retargeter
  // is constructed (which captures its own bind-pose copy) keeps the two
  // sources of bind data in sync.
  const srcHipBone = srcSkeleton.bones.find((b) => SOURCE_PATTERNS.Hips.test(b.name));
  const trgHipBone = trgSkeleton.bones.find((b) => trgPatterns.Hips.test(b.name));
  if (!srcHipBone || !trgHipBone) {
    console.warn('[cc-retarget] hip bone resolution failed post-construct — skipping');
    return null;
  }

  donor.root.updateMatrixWorld(true);
  trgRoot.updateMatrixWorld(true);

  const srcHipBindLocal = new Vector3().copy(srcHipBone.position);
  const trgHipBindLocal = new Vector3().copy(trgHipBone.position);
  const srcHipBindWorld = new Vector3();
  const trgHipBindWorld = new Vector3();
  srcHipBone.getWorldPosition(srcHipBindWorld);
  trgHipBone.getWorldPosition(trgHipBindWorld);

  const srcHipParentMatrixWorld = new Matrix4();
  const trgHipParentMatrixWorldInv = new Matrix4();
  if (srcHipBone.parent) srcHipParentMatrixWorld.copy(srcHipBone.parent.matrixWorld);
  if (trgHipBone.parent) trgHipParentMatrixWorldInv.copy(trgHipBone.parent.matrixWorld).invert();

  console.log(
    `[cc-retarget] hip bind · src "${srcHipBone.name}" world=(${srcHipBindWorld.x.toFixed(3)}, ${srcHipBindWorld.y.toFixed(3)}, ${srcHipBindWorld.z.toFixed(3)}) `
    + `· trg "${trgHipBone.name}" world=(${trgHipBindWorld.x.toFixed(3)}, ${trgHipBindWorld.y.toFixed(3)}, ${trgHipBindWorld.z.toFixed(3)})`,
  );

  return {
    retargeter,
    srcHipName: srcHipBone.name,
    trgHipName: trgHipBone.name,
    srcHipBindLocal,
    trgHipBindLocal,
    srcHipBindWorld,
    trgHipBindWorld,
    srcHipParentMatrixWorld,
    trgHipParentMatrixWorldInv,
  };
}

// ── Per-clip processing ───────────────────────────────────────────────────

/**
 * Run a Mixamo-named clip through the cc-base retarget pipeline.
 *
 * The returned clip has:
 *   - All rotation tracks renamed + retargeted to cc-base bones
 *     (via `AnimationRetargeting.retargetAnimation`).
 *   - A single position track for the cc-base hip, computed from the
 *     source clip's hip-position track via world-space delta.
 *
 * Mutations to the input `clip`: this function anchors the original clip's
 * `Hips.position` track in place (so the source-side mixer sees an
 * anchored clip too — useful when the same clip is also bound to a
 * Mixamo character for comparison). Pass a clone if the caller wants to
 * keep the unmodified original.
 */
export function processClipForCcRig(
  clip: AnimationClip,
  ctx: CcRetargetContext,
): AnimationClip {
  // 1. Anchor the original clip's hip-position track to source bind. After
  //    this, frame 0 of `Hips.position` = `ctx.srcHipBindLocal`. The same
  //    anchored values feed into the world-space hip retarget below.
  const hipPosTrack = clip.tracks.find((t) => HIP_POS_TRACK_RE.test(t.name));
  if (hipPosTrack) {
    const v = (hipPosTrack as { values: Float32Array }).values;
    const dx = ctx.srcHipBindLocal.x - v[0];
    const dy = ctx.srcHipBindLocal.y - v[1];
    const dz = ctx.srcHipBindLocal.z - v[2];
    for (let i = 0; i < v.length; i += 3) {
      v[i]     += dx;
      v[i + 1] += dy;
      v[i + 2] += dz;
    }
  }

  // 2. Build a position-stripped clone for the rotation retarget. The
  //    library's own retargetPositionTrack would otherwise emit a buggy
  //    hip-position track on the result. We do our own below.
  const clipForRetarget = clip.clone();
  clipForRetarget.tracks = clipForRetarget.tracks.filter(
    (t) => !t.name.endsWith('.position'),
  );

  // 2b. Strip any `mixamorig:?` prefix from track bone names so they line
  //     up with the donor (Lewis) skeleton's bare bone names — that's
  //     what the retargeter's `boneNameMap` keys are built from. Two
  //     classes of clip arrive here:
  //       - HY-Motion GLB clips → tracks already bare (`Hips.quaternion`).
  //         This pass is a no-op for them.
  //       - Mixamo library FBX clips (e.g. Standing Idle.fbx loaded via
  //         FBXLoader) → tracks ship as `mixamorigHips.quaternion` /
  //         `mixamorig:Hips.quaternion`. Without stripping, the lookup
  //         `boneNameMap["mixamorigHips"]` returns undefined, every
  //         rotation track is dropped, and `retargetAnimation` returns
  //         an empty clip → cc-base character holds T-pose.
  //     This is the missing step that makes Riya / Anne play any of the
  //     ~2,200 Mixamo library clips, including the default standing idle.
  for (const track of clipForRetarget.tracks) {
    track.name = track.name.replace(/^mixamorig:?/, '');
  }

  // 3. Bind-pose-aware rotation retarget.
  const retargeted = ctx.retargeter.retargetAnimation(clipForRetarget);

  // Sanity log: if the retargeter emits 0 tracks the clip will have
  // duration 0 and play for ~1 frame. This usually means the clip's
  // track-bone names don't match any bone in the donor skeleton, which
  // in turn usually means somebody ran `normalizeMixamoTracks` on the
  // clip before sending it through here.
  if (retargeted.tracks.length === 0 && clip.tracks.length > 0) {
    console.warn(
      `[cc-retarget] retargetAnimation produced 0 tracks from a ${clip.tracks.length}-track input. `
      + `Likely cause: clip track-bone names ("${clip.tracks[0].name}") don't match any donor bone. `
      + `Check that the caller is NOT prepending the "mixamorig" prefix to clip tracks.`,
    );
  }

  // 4. Inject world-space hip-position track on the cc-base hip. Per
  //    frame: srcLocal → srcWorld → world delta from src bind world →
  //    + trg bind world → trg local.
  if (hipPosTrack) {
    const srcVals = (hipPosTrack as { values: Float32Array }).values;
    const newVals = new Float32Array(srcVals.length);
    const tmp = new Vector3();
    for (let i = 0; i < srcVals.length; i += 3) {
      tmp.set(srcVals[i], srcVals[i + 1], srcVals[i + 2]);
      tmp.applyMatrix4(ctx.srcHipParentMatrixWorld);
      tmp.sub(ctx.srcHipBindWorld);
      tmp.add(ctx.trgHipBindWorld);
      tmp.applyMatrix4(ctx.trgHipParentMatrixWorldInv);
      newVals[i]     = tmp.x;
      newVals[i + 1] = tmp.y;
      newVals[i + 2] = tmp.z;
    }
    retargeted.tracks.push(new VectorKeyframeTrack(
      `${ctx.trgHipName}.position`,
      (hipPosTrack as { times: Float32Array }).times,
      newVals,
    ));
  }

  return retargeted;
}
